import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Card from "../../UI/Card";
import DisasterListItem from "./DisasterListItem";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

const PAGE_SIZE = 20;

function normalizeDsox(ox) {
  const v = ox == null ? "" : String(ox).trim();
  if (v === "1" || v === "true") return "1";
  return "0";
}

function filterAuthorized(responseData, oxFilter = "all") {
  const userGrade = localStorage.usergrade;
  const loginid = (localStorage.loginid || "").toString().trim();
  const hq = (localStorage.hq || "").toString().trim();
  const loadedList = [];
  const raw = Array.isArray(responseData)
    ? responseData
    : Object.keys(responseData || {}).map((k) => responseData[k]);
  for (const disaster of raw) {
    if (!disaster) continue;
    const dsidVal = (disaster.dsid || disaster.dsnum || "").toString().trim();
    const dshqVal = (disaster.dshq || "").toString().trim();
    const isAuthorized =
      loginid === "mftest" ||
      (userGrade == 1 && loginid === dsidVal) ||
      (userGrade == 0 && hq === dshqVal);
    if (!isAuthorized) continue;
    const ox = normalizeDsox(disaster.dsox);
    if (oxFilter !== "all" && ox !== oxFilter) continue;
    loadedList.push({
      id: disaster.snum,
      name: disaster.dsnm,
      ipdt: disaster.dsipdt,
      ox,
      addr: disaster.dsaddr,
      dsid: disaster.dsnum,
      lat: disaster.dslat,
      lon: disaster.dslon,
    });
  }
  return loadedList;
}

/** 본부관리자(mfhq) — 진행/종료 목록 필터 표시 */
export function isMfhqLogin() {
  const loginid = (localStorage.loginid || "").trim().toLowerCase();
  return loginid === "mfhq" || localStorage.usergrade == 0;
}

const DiasterLists = (props) => {
  const [list, setList] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [oxFilter, setOxFilter] = useState("all");
  const scrollRef = useRef(null);
  const loadingRef = useRef(false);
  const listRef = useRef([]);
  const soloDisasterId = props.soloDisasterId ?? null;
  const setSoloDisasterId = props.onSoloChange || (() => {});
  const showOxFilter = useMemo(() => isMfhqLogin(), []);

  const getHqParam = () =>
    encodeURIComponent((localStorage.hq || "").toString().trim());

  /** 자원등록앱과 동일 — 진행 중 재난 전체 (본부별) */
  const fetchActiveList = useCallback(async () => {
    const hq = getHqParam();
    const wstring = `${props.apiAddr}/get/getactivedslist?hq=${hq}`;
    const response = await fetch(wstring);
    const responseData = await response.json();
    const loadedList = filterAuthorized(responseData, "1");
    listRef.current = loadedList;
    setList(loadedList);
    setHasMore(false);
    setOffset(0);
  }, [props.apiAddr]);

  const fetchPage = useCallback(
    async (offsetVal, append) => {
      if (!props.apiAddr || loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        const activeOxFilter = showOxFilter ? oxFilter : "all";
        const hqQuery = showOxFilter ? `&hq=${getHqParam()}` : "";
        const oxQuery =
          activeOxFilter !== "all" ? `&dsox=${activeOxFilter}` : "";
        let currentOffset = offsetVal;
        let merged = append ? [...listRef.current] : [];
        let lastRawLen = PAGE_SIZE;

        for (let skip = 0; skip < 50; skip += 1) {
          const wstring = `${props.apiAddr}/get/dslist?limit=${PAGE_SIZE}&offset=${currentOffset}${oxQuery}${hqQuery}`;
          const response = await fetch(wstring);
          const responseData = await response.json();
          const raw = Array.isArray(responseData)
            ? responseData
            : Object.keys(responseData || {}).map((k) => responseData[k]);
          const loadedList = filterAuthorized(responseData, activeOxFilter);
          lastRawLen = raw.length;
          merged = [...merged, ...loadedList];
          currentOffset += PAGE_SIZE;

          const atEnd = lastRawLen < PAGE_SIZE;
          let needMore = false;
          if (activeOxFilter !== "all" && !atEnd) {
            if (loadedList.length === 0) {
              needMore = true;
            } else if (!append && merged.length < PAGE_SIZE) {
              needMore = true;
            }
          }
          if (!needMore) break;
        }

        listRef.current = merged;
        setList(merged);
        setHasMore(lastRawLen >= PAGE_SIZE);
        setOffset(currentOffset);
      } catch (e) {
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [props.apiAddr, oxFilter, showOxFilter]
  );

  const loadList = useCallback(
    async (append = false, offsetVal = 0) => {
      const activeOxFilter = showOxFilter ? oxFilter : "all";
      if (activeOxFilter === "1") {
        await fetchActiveList();
        return;
      }
      await fetchPage(offsetVal, append);
    },
    [showOxFilter, oxFilter, fetchActiveList, fetchPage]
  );

  useEffect(() => {
    listRef.current = list;
  }, [list]);

  useEffect(() => {
    setList([]);
    setOffset(0);
    setHasMore(true);
    loadList(false, 0);
  }, [props.refresh, oxFilter, loadList]);

  const handleScroll = useCallback(() => {
    if (oxFilter === "1") return;
    const el = scrollRef.current;
    if (!el || !hasMore || loading) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      loadList(true, offset);
    }
  }, [hasMore, loading, offset, loadList, oxFilter]);

  useEffect(() => {
    if (soloDisasterId != null && !list.some((l) => String(l.id) === String(soloDisasterId))) {
      setSoloDisasterId(null);
    }
  }, [list, soloDisasterId, setSoloDisasterId]);

  const filteredList =
    showOxFilter && oxFilter !== "all"
      ? list.filter((l) => normalizeDsox(l.ox) === oxFilter)
      : list;
  const displayList = soloDisasterId
    ? filteredList.filter((l) => String(l.id) === String(soloDisasterId))
    : filteredList;
  const nowList = displayList.map((l) => (
    <DisasterListItem
      key={l.id}
      id={l.id}
      name={l.name}
      ipdt={l.ipdt}
      ox={l.ox}
      addr={l.addr}
      dsid={l.dsid}
      lat={l.lat}
      lon={l.lon}
      onDataChanged={props.onDataChanged}
      apiAddr={props.apiAddr}
      onStatus={props.onStatus}
      onSelectDS={props.onSelectDS}
      onShowRDV={props.onShowRDV}
      hdmf={props.hdmf}
      mapfresh={props.mapfresh}
      soloDisasterId={soloDisasterId}
      onSoloChange={setSoloDisasterId}
      onCollapsePanel={props.onCollapsePanel}
    />
  ));

  const isSolo = soloDisasterId != null;
  return (
    <Card
      style={{
        height: isSolo ? "auto" : "580px",
        maxHeight: isSolo ? "320px" : "580px",
        minHeight: isSolo ? "100px" : undefined,
        backgroundColor: "yellow",
        flexShrink: isSolo ? 0 : undefined,
      }}
    >
      {showOxFilter && !isSolo && (
        <Box
          sx={{
            px: 0.5,
            pt: 0.75,
            pb: 0.5,
            borderBottom: "1px solid rgba(0,0,0,0.12)",
            backgroundColor: "rgba(255,255,255,0.55)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 0.5, color: "#2e7d32", fontWeight: 700 }}
          >
            진행단계
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={oxFilter}
            onChange={(_, v) => v != null && setOxFilter(v)}
          >
            <ToggleButton
              value="all"
              sx={{
                flex: 1,
                py: 0.4,
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "none",
                color: "#33691e",
                borderColor: "#81c784",
                "&.Mui-selected": {
                  bgcolor: "#c8e6c9",
                  color: "#1b5e20",
                  borderColor: "#66bb6a",
                },
              }}
            >
              전체
            </ToggleButton>
            <ToggleButton
              value="1"
              sx={{
                flex: 1,
                py: 0.4,
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "none",
                color: "#b71c1c",
                borderColor: "#ef5350",
                "&.Mui-selected": {
                  bgcolor: "#e53935",
                  color: "#fff",
                  borderColor: "#c62828",
                },
              }}
            >
              진행
            </ToggleButton>
            <ToggleButton
              value="0"
              sx={{
                flex: 1,
                py: 0.4,
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "none",
                color: "#212121",
                borderColor: "#757575",
                "&.Mui-selected": {
                  bgcolor: "#212121",
                  color: "#fff",
                  borderColor: "#000",
                },
              }}
            >
              종료
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          overflowY: "auto",
          height: "100%",
          minHeight: 0,
        }}
      >
        {nowList}
        {!loading && filteredList.length === 0 && (
          <div style={{ padding: "12px", textAlign: "center", fontSize: "0.85rem", color: "#666" }}>
            {oxFilter === "1"
              ? "진행 중인 재난이 없습니다."
              : oxFilter === "0"
                ? "종료된 재난이 없습니다."
                : "표시할 재난이 없습니다."}
          </div>
        )}
        {loading && (
          <div style={{ padding: "8px", textAlign: "center", fontSize: "0.85rem", color: "#666" }}>
            불러오는 중…
          </div>
        )}
      </div>
    </Card>
  );
};

export default DiasterLists;
